// src/components/common/RichTextEditor.jsx
import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Code from "@tiptap/extension-code";
import DOMPurify from "dompurify";
import FontSize from "../../../../extensions/Fontsize";

import alignLeft from "../../../../assets/svg/alignLeft.svg";
import alignRight from "../../../../assets/svg/alignRight.svg";
import alignCenter from "../../../../assets/svg/alignCenter.svg";
import alignJustify from "../../../../assets/svg/alignJustify.svg";
import formatBold from "../../../../assets/svg/formatBold.svg";
import formatItalic from "../../../../assets/svg/formatItalic.svg";
import formatListBulleted from "../../../../assets/svg/formatListBulleted.svg";
import formatListNumbered from "../../../../assets/svg/formatListNumbered.svg";
import formatUnderline from "../../../../assets/svg/formatUnderline.svg";
import link from "../../../../assets/svg/link.svg";
import palette from "../../../../assets/svg/palette.svg";

import { fontFamilies, fontSizes, presetColors } from "./tiersConstants";

const RichTextEditor = ({ initialHtml = "", onChangeHtml, className = "" }) => {
  const [showPalette, setShowPalette] = useState(false);
  const [customHex, setCustomHex] = useState("#000000");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: true,
        orderedList: true,
        listItem: true,
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      FontFamily.configure({ types: ["textStyle"] }),
      FontSize.configure({ types: ["textStyle"] }),
      Underline,
      Highlight.configure({ multicolor: true }),
      Code,
      TextAlign.configure({ types: ["paragraph", "heading", "listItem"] }),
      Link.configure({ openOnClick: false }),
    ],
    content: DOMPurify.sanitize(initialHtml || ""),
    onUpdate({ editor }) {
      const dirty = editor.getHTML();
      const sanitized = DOMPurify.sanitize(dirty);
      onChangeHtml?.(sanitized);
    },
  });

  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.getHTML();
    const sanitized = DOMPurify.sanitize(initialHtml || "");

    if (sanitized && sanitized !== currentContent) {
      editor.commands.setContent(sanitized, false);
    }
  }, [initialHtml, editor]);

  useEffect(() => {
    return () => editor?.destroy?.();
  }, [editor]);

  if (!editor) return null;

  const applyFontSize = (size) => editor.chain().focus().setFontSize(size).run();
  const applyFontFamily = (font) => editor.chain().focus().setFontFamily(font).run();

  const promptForLink = () => {
    const url = window.prompt("Enter URL (https://...)");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className={`bg-white border rounded-lg ${className}`}>
      {/* Toolbar */}
      <div className="px-3 py-2 bg-catskill rounded-lg border-b flex items-center flex-wrap gap-2">

        {/* Text Align */}
        <div className="flex items-center gap-2 pr-2 border-r-2 border-cadetGray">
          {["left", "center", "right", "justify"].map((align) => (
            <button
              key={align}
              onClick={() => editor.chain().focus().setTextAlign(align).run()}
              className={`${editor.isActive({ textAlign: align }) ? "bg-gray-200 rounded" : ""}`}
              title={`Align ${align}`}
            >
              <img
                src={
                  align === "left" ? alignLeft :
                  align === "center" ? alignCenter :
                  align === "right" ? alignRight :
                  alignJustify
                }
                alt={`align ${align}`}
                className="w-5"
              />
            </button>
          ))}
        </div>

        {/* Format text */}
        <div className="flex items-center gap-2 px-2 border-r-2 border-cadetGray">
          <button onClick={() => editor.chain().focus().toggleBold().run()} className={`${editor.isActive("bold") ? "bg-gray-200 rounded" : ""}`} title="Bold">
            <img src={formatBold} alt="bold" className="w-5" />
          </button>
          <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`${editor.isActive("italic") ? "bg-gray-200 rounded" : ""}`} title="Italic">
            <img src={formatItalic} alt="italic" className="w-5" />
          </button>
          <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={`${editor.isActive("underline") ? "bg-gray-200 rounded" : ""}`} title="Underline">
            <img src={formatUnderline} alt="underline" className="w-5" />
          </button>
          <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${editor.isActive("bulletList") ? "bg-gray-200 rounded" : ""}`} title="Bulleted list">
            <img src={formatListBulleted} alt="bulleted list" className="w-5" />
          </button>
          <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${editor.isActive("orderedList") ? "bg-gray-200 rounded" : ""}`} title="Numbered list">
            <img src={formatListNumbered} alt="numbered list" className="w-5" />
          </button>
        </div>

        {/* Links & Color */}
        <div className="flex items-center gap-2 px-2 border-r-2 border-cadetGray">
          <button onClick={promptForLink} title="Insert link">
            <img src={link} alt="link" className="w-5" />
          </button>

          <div className="relative">
            <button onClick={() => setShowPalette(!showPalette)} title="Text Color">
              <img src={palette} alt="palette" className="w-5" />
            </button>

            {showPalette && (
              <div className="absolute top-8 left-0 z-50 bg-white rounded-lg shadow-xl p-3 w-60 border border-gray-100">
                <div className="space-y-2 pb-2 border-b border-gray-200">
                  {presetColors.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex justify-between gap-1">
                      {row.map((color, colIndex) => (
                        <button
                          key={colIndex}
                          className="w-6 h-6 border border-[#B7B7B7] rounded-full shadow-sm hover:scale-110 hover:shadow-md transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => {
                            editor.chain().focus().setColor(color).run();
                            setShowPalette(false);
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>

                <div className="pt-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Custom</p>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={customHex}
                      onChange={(e) => setCustomHex(e.target.value)}
                      placeholder="#000000"
                      className="border border-gray-300 rounded-md px-2 py-1 w-full text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                      onBlur={() => {
                        if (/^#([0-9A-F]{3}){1,2}$/i.test(customHex)) editor.chain().focus().setColor(customHex).run();
                      }}
                    />
                    <button
                      className="w-6 h-6 rounded-full border border-[#B7B7B7] shadow-sm hover:shadow-md hover:scale-110 transition-transform"
                      style={{ backgroundColor: customHex || "#ffffff" }}
                      onClick={() => {
                        if (/^#([0-9A-F]{3}){1,2}$/i.test(customHex)) editor.chain().focus().setColor(customHex).run();
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Font size & family */}
        <div className="flex items-center gap-2 pl-2">
          <select defaultValue={fontSizes[1]} onChange={(e) => applyFontSize(e.target.value)} className="outline-none border rounded px-2 py-1 bg-white" title="Font size">
            {fontSizes.map((s) => (
              <option key={s} value={s}>{s.replace("px", "")} px</option>
            ))}
          </select>

          <select defaultValue={fontFamilies[0]} onChange={(e) => applyFontFamily(e.target.value)} className="outline-none border rounded px-2 py-1 bg-white" title="Font family">
            {fontFamilies.map((f) => (
              <option key={f} value={f}>{f.split(",")[0].replace(/['"]/g, "")}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Editor */}
      <div className="px-1 py-1">
        <EditorContent editor={editor} className="max-h-[240px] overflow-scroll w-full rich-text-editor" />
      </div>
    </div>
  );
};

export default RichTextEditor;
